/**
 * ProfileChips — UI-02 + D-UI-PROFILE-01/02 + GUARD-03 defense-in-depth.
 *
 * 4 chips primarios sticky: Adulto / Niño / Embarazo / Lactancia (ícono de línea + texto, 20-oct).
 * Tap "Niño" expande row secundaria etaria (4 chips → child_3y/5y/8y/12y).
 *
 * Default: ningún chip activo (profile === null). Backend GUARD-03 rechaza
 * queries sin perfil; este UI permite tap explícito por la farmacéutica.
 *
 * Hotfix UAT 2026-05-08 (Issue A — profile gate frontend safety):
 * cuando profile=null el row de chips primarios pulsa suavemente y se
 * muestra helper text "Selecciona quién es el paciente para continuar"
 * para guiar a la farmacéutica antes de habilitar el composer.
 * Drimian P5: helper inline + pulse — NO modal popup (kiosko UX).
 *
 * Tap targets ≥44px por chip (UI-SPEC §Spacing locked).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Icon } from './icons'
import type { ProfileType } from '../types/profile'

interface Props {
  profile: ProfileType | null
  onSelectProfile: (p: ProfileType) => void
}

interface PrimaryChip {
  label: string
  icono: ReactNode
  /** Si está presente, tap setea perfil directo. Sin él, expande secundaria. */
  value?: ProfileType
  /** Identificador del grupo de chips secundarios a expandir. */
  expand?: 'child'
}

const PRIMARY_CHIPS: PrimaryChip[] = [
  { label: 'Adulto', icono: <Icon.Adulto width={16} height={16} aria-hidden="true" style={{ flexShrink: 0 }} />, value: 'adult' },
  { label: 'Niño', icono: <Icon.Nino width={16} height={16} aria-hidden="true" style={{ flexShrink: 0 }} />, expand: 'child' },
  { label: 'Embarazo', icono: <Icon.Embarazo width={16} height={16} aria-hidden="true" style={{ flexShrink: 0 }} />, value: 'pregnancy' },
  { label: 'Lactancia', icono: <Icon.Lactancia width={16} height={16} aria-hidden="true" style={{ flexShrink: 0 }} />, value: 'lactation' },
]

interface ChildChip {
  label: string
  value: ProfileType
}

const CHILD_CHIPS: ChildChip[] = [
  { label: '0–4 años', value: 'child_3y' },
  { label: '5–7 años', value: 'child_5y' },
  { label: '8–11 años', value: 'child_8y' },
  { label: '12+ años', value: 'child_12y' },
]

const CHILD_PROFILES: ProfileType[] = ['child_3y', 'child_5y', 'child_8y', 'child_12y']

function isChildProfile(p: ProfileType | null): boolean {
  return p !== null && CHILD_PROFILES.includes(p)
}

interface ChipBtnProps {
  active: boolean
  label: string
  icono?: ReactNode
  onClick: () => void
  ariaPressed: boolean
}

function ChipBtn({ active, label, icono, onClick, ariaPressed }: ChipBtnProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-pressed={ariaPressed}
      aria-checked={ariaPressed}
      onClick={onClick}
      className="chip-perfil text-[14px] font-medium whitespace-nowrap transition-all inline-flex items-center gap-1.5"
      style={{
        minHeight: 44,
        minWidth: 44,
        flexShrink: 0,
        padding: '8px 12px',
        borderRadius: 999,
        border: '1px solid',
        borderColor: active ? 'var(--color-brand)' : 'var(--color-border-strong)',
        background: active ? 'var(--color-brand-soft)' : 'var(--color-surface)',
        color: active ? 'var(--color-brand-ink)' : 'var(--color-text)',
        cursor: 'pointer',
        // 20-oct: sin `outline: none`; el anillo sale solo con teclado (:focus-visible en index.css),
        // así no parece que dos chips quedan marcados al tocar
      }}
    >
      {icono}
      {label}
    </button>
  )
}

export function ProfileChips({ profile, onSelectProfile }: Props) {
  // Expand row de niño si: hay perfil child_*, o tocó Niño explícitamente.
  const [childExpanded, setChildExpanded] = useState<boolean>(isChildProfile(profile))
  // 21-oct (prueba en producción): tras "Nuevo cliente" las edades quedaban abiertas y el primer toque en
  // Niño las cerraba; sin paciente, se cierran
  useEffect(() => {
    if (profile === null) setChildExpanded(false)
  }, [profile])

  const handlePrimary = (chip: PrimaryChip) => {
    if (chip.expand === 'child') {
      setChildExpanded((v) => !v)
      return
    }
    if (chip.value) {
      setChildExpanded(false)
      onSelectProfile(chip.value)
    }
  }

  const handleChild = (value: ProfileType) => {
    onSelectProfile(value)
  }

  // Hotfix UAT 2026-05-08 (Issue A) — pulse + helper text cuando profile=null.
  // Defense-in-depth con backend GUARD-03 + Composer disabled. Drimian P7.
  const noneSelected = profile === null

  return (
    <div
      className="sticky top-0 z-10 px-4 py-3 grid gap-2"
      style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        backdropFilter: 'saturate(180%) blur(6px)',
      }}
      role="radiogroup"
      aria-label="Perfil del paciente"
    >
      <div
        className={`flex items-center gap-2 overflow-x-auto ${noneSelected ? 'motion-safe:animate-pulse' : ''}`}
        style={{ scrollbarWidth: 'none' }}
      >
        {PRIMARY_CHIPS.map((chip) => {
          const active =
            chip.value !== undefined
              ? profile === chip.value
              : chip.expand === 'child' && (childExpanded || isChildProfile(profile))
          return (
            <ChipBtn
              key={chip.label}
              active={active}
              ariaPressed={chip.value !== undefined ? profile === chip.value : false}
              label={chip.label}
              icono={chip.icono}
              onClick={() => handlePrimary(chip)}
            />
          )
        })}
      </div>

      {childExpanded && (
        <div className="flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {CHILD_CHIPS.map((c) => (
            <ChipBtn
              key={c.value}
              active={profile === c.value}
              ariaPressed={profile === c.value}
              label={c.label}
              onClick={() => handleChild(c.value)}
            />
          ))}
        </div>
      )}

      {noneSelected && (
        <div
          role="status"
          aria-live="polite"
          className="text-[13px]"
          style={{
            color: 'var(--color-text-muted, #6b7280)',
            paddingLeft: 2,
          }}
        >
          Elegí quién es el paciente para continuar
        </div>
      )}
    </div>
  )
}
